import { HTMLAttributes, useRef, useState } from 'react'
import { useWindowSize } from 'react-use'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'

import { useLayoutStore } from '@/store/layout'

import SVGIcon from './svg-icon'
import Typography from './typography'
import { cn } from '@/lib/utils'

type BurgerProps = HTMLAttributes<HTMLButtonElement> & {
  transitionDuration: number
  labelMenu?: string
  labelClose?: string
  onClick?: () => void
}

const Burger = ({
  transitionDuration,
  labelMenu,
  labelClose,
  className
}: BurgerProps) => {
  const root = useRef(null!)
  const menuOpen = useLayoutStore((state) => state.menuOpen)
  const setLayoutState = useLayoutStore((state) => state.setLayoutState)
  const [isAnimating, setIsAnimating] = useState(false)

  const winSize = useWindowSize()

  const handleClick = () => {
    if (isAnimating) return
    setLayoutState({ menuOpen: !menuOpen })
    setIsAnimating(true)

    gsap.delayedCall(transitionDuration / 1000, () => {
      setIsAnimating(false)
    })
  }

  useGSAP(
    () => {
      const wOpen = gsap.getProperty(
        '[data-motion-label] span:nth-child(1)',
        'scrollWidth'
      )

      const wClose = gsap.getProperty(
        '[data-motion-label] span:nth-child(2)',
        'scrollWidth'
      )

      gsap.to('[data-motion-icon]', {
        rotate: !menuOpen ? 0 : 135,
        duration: 1,
        ease: 'power3.inOut'
      })
      gsap.to('[data-motion-label]', {
        y: !menuOpen ? '0%' : '-100%',
        width: !menuOpen ? wOpen : wClose,
        duration: 1,
        ease: 'power3.inOut'
      })
    },
    {
      scope: root,
      dependencies: [menuOpen, winSize]
    }
  )

  return (
    <button
      className={cn(
        'relative inline-flex h-[4.5rem] w-auto items-center space-x-[0.8rem] rounded-[0.8rem] bg-black px-[2rem]',
        className
      )}
      onClick={handleClick}
      data-motion-menu-button
      ref={root}
    >
      <div className={cn('block')} data-motion-icon>
        <SVGIcon
          symbol="ico-close"
          className="size-[.8rem] translate-y-[-.1rem] fill-white"
        />
      </div>
      <span className="flex h-[1.28rem] overflow-hidden">
        <span className="item-start flex flex-col" data-motion-label>
          <Typography className="hidden h-[1.28rem] w-[3.5rem] text-[1.28rem] font-bold capitalize leading-[1] tracking-[0.05rem] md:flex">
            {labelMenu}
          </Typography>
          <Typography className="hidden h-[1.28rem] w-[3.5rem] text-[1.28rem] font-bold capitalize leading-[1] tracking-[0.05rem] opacity-40 md:flex">
            {labelClose}
          </Typography>
        </span>
      </span>
    </button>
  )
}

export default Burger
